import React from "react";
import type { FunctionComponent } from "react";
import { useEffect, useMemo, useState, useRef } from "react";
import {
    ContentWrapper,
    CtaWrapper,
    StyledBox,
    TitleWrapper,
} from "app/pages/website-analysis/components/qualification-sidebar/components/StyledComponents";
import { WidgetTitle } from "app/components/widget-title/WidgetTitle";
import QualificationSideBarContext from "app/components/QualificationSideBar/QualificationSidebarContext";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { i18nFilter } from "app/filters/i18nFilter";
import { DefaultFetchService } from "app/services/fetchService";
import DurationService from "app/services/DurationService";
import * as _ from "lodash";
import utils, { orderedTrafficSources } from "app/Shared/utils";
import { NoDataLandscape } from "pro-features/components/NoData/src/NoData";
import Chart from "pro-features/components/Chart/src/Chart";
import { translateSourceText } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartConfig";
import { CHART_COLORS } from "app/constants/ChartColors";
import { createGlobalStyle } from "styled-components";
import { percentsFilter } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/ChannelsOverviewBarChartContainerFormat";
import { colorsPalettes } from "@similarweb/styles";
import { hasAccessToPackage } from "app/common/services/solutions2Helper";
import ReactDOMServer from "react-dom/server";
import { ChartLabelOpenNewWindowWrapper } from "app/pages/website-analysis/traffic-sources/mmx/components/ChannelsOverviewBarChart/StyledComponents";
import { getSubTitleFilters } from "app/pages/website-analysis/components/qualification-sidebar/components/helpers";
import { ComponentsLoader } from "app/pages/website-analysis/components/qualification-sidebar/components/TitleLoader";
import { IconButton } from "@similarweb/ui-components";
import { PlainTooltip } from "@similarweb/ui-components";
import type { ISideBarComponentProps } from "app/pages/website-analysis/components/qualification-sidebar/QualificationSideBarContainer";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

const CHANNEL_TRACKING_EVENT_NAMES: Partial<Record<string, string>> = {
    ads: "QualificationSidebar/channels/display ads",
    search: "QualificationSidebar/channels/paid search",
    organic: "QualificationSidebar/channels/organic search",
    social: "QualificationSidebar/channels/social",
    referrals: "QualificationSidebar/channels/referral",
};

const GlobalStyle = createGlobalStyle`
  .highcharts-tooltip-container{
    z-index: 9999 !important;
  }
`;

const TrafficSources: FunctionComponent<ISideBarComponentProps> = ({
    webSource,
    duration,
    country,
    onEmptyData,
    isWWOApiController,
    componentId,
}) => {
    const i18n = i18nFilter();
    const ctrl = isWWOApiController ? "WorldwideOverviewDesktop" : "WebsiteOverviewDesktop";
    const { qualificationSideBarSelection } = React.useContext(QualificationSideBarContext);
    const domain = qualificationSideBarSelection?.data?.domain;
    const fetchService = DefaultFetchService.getInstance();
    const durationObject = DurationService.getDurationData(duration, undefined, componentId);
    const { from, to } = durationObject.forAPI;
    const title = i18n("qualification.sidebar.traffic.sources.title");
    const subTitleFilters = useMemo(
        () => getSubTitleFilters({ webSource, duration, country }),
        [webSource, duration, country],
    );
    const tooltip = i18n("qualification.sidebar.traffic.sources.title.tooltip");
    const [data, setData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isError, setIsError] = useState<boolean>(false);
    const chartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        async function fetchData() {
            setData(null);
            setIsLoading(true);
            try {
                const response = await fetchService.get<{ Data: object[] }>(
                    `widgetApi/${ctrl}/TrafficSourcesOverview/PieChart`,
                    {
                        country,
                        from,
                        includeSubDomains: true,
                        isWindow: false,
                        keys: domain,
                        timeGranularity: "Monthly",
                        to,
                        webSource,
                    },
                );
                setData(transformData(response["Data"]));
            } catch (e) {
                setIsError(true);
            } finally {
                setIsLoading(false);
            }
        }
        fetchData();
    }, [domain]);

    useEffect(() => {
        // getting JQuery element
        const $chart = $(chartRef.current);
        $chart.on("click", "[data-ts-category]", (evt) => {
            const trafficSource = evt.target.textContent.replace(/\s+/g, "-").toLowerCase(); // Replace spaces with dashes and make all letters lower-case
            const eventName = CHANNEL_TRACKING_EVENT_NAMES[trafficSource];
            if (eventName) {
                allTrackers.dispatchTrackEvent("Internal Link", "click", eventName);
            }
        });

        return () => {
            $chart.off("click", "[data-ts-category]");
        };
    }, []);

    const checkAreTrafficSourcesEmpty = (mmXTrafficSources) => {
        let areTrafficSourcesEmpty = true;
        const sourcesValuesArray = Object.values(mmXTrafficSources);
        let i = 0;
        while (i < sourcesValuesArray.length && areTrafficSourcesEmpty) {
            if (sourcesValuesArray[i]) {
                areTrafficSourcesEmpty = false;
            }
            i++;
        }
        return areTrafficSourcesEmpty;
    };

    const getTotalVisits = (data) => Object.values<number>(data).reduce((sum: number, val) => sum + val, 0);

    const getPointY = (pointValue = 0, totalVisits) => {
        if (!totalVisits) {
            return null;
        } else {
            return 100 * (pointValue / totalVisits);
        }
    };

    const transformData = (mmXTrafficSources) => {
        const sitesDataWithTotalVisits = checkAreTrafficSourcesEmpty(mmXTrafficSources)
            ? {}
            : Object.entries(mmXTrafficSources).reduce((newDataObject, [domain, trafficSources]) => {
                  trafficSources = trafficSources || {};
                  const totalVisits = getTotalVisits(trafficSources);

                  return {
                      ...newDataObject,
                      [domain]: {
                          trafficSources: trafficSources,
                          totalVisits: totalVisits,
                      },
                  };
              }, {});

        const transformedData = _.isEmpty(sitesDataWithTotalVisits)
            ? {}
            : utils.fillMissingTrafficSources(sitesDataWithTotalVisits, webSource === devicesTypes.MOBILE, from);

        if (_.isEmpty(transformedData)) {
            return null;
        } else {
            const sitesDataPoints = [];
            if (transformedData[domain]) {
                const { trafficSources, totalVisits } = transformedData[domain];

                sitesDataPoints.push({
                    name: domain,
                    data: orderedTrafficSources
                        .filter((source) => trafficSources.hasOwnProperty(source))
                        .map((sourceName) => ({
                            key: translateSourceText(sourceName),
                            y: getPointY(trafficSources[sourceName], totalVisits),
                            color: CHART_COLORS.trafficSourcesColorsBySourceMMX[sourceName],
                        })),
                });
            }

            return sitesDataPoints;
        }
    };

    const getCategories = () => {
        const chartItemsInfo = utils.volumesAndSharesSplited;
        const categories = data[0].data.map((cat) => ({ key: cat.key }));
        return _.sortBy(categories, chartItemsInfo.sort).map((cat: { key: string }) => {
            const key = cat.key;
            const item = chartItemsInfo.order[key];
            return {
                key,
                item,
                title: i18n(item?.title) || key,
            };
        });
    };

    const getRenderCategoryLabel = () => {
        return function () {
            const { key, item, title } = this.value;
            const categoryArray = title.split(" ");
            let categoryHTML = title;

            if (item?.state) {
                const itemState =
                    item.title === "utils.paidSearch" ? "digitalsuite_website_search_overview" : item.state;
                let href = proNavigator.href(itemState, {
                    ...proNavigator.getParams(),
                    key: domain,
                    isWWW: "*",
                });
                const itemPackage = proNavigator.getPackageName(itemState);
                if (item?.queryParams) {
                    href = `${href}&${item.queryParams}`;
                }
                const chosenTitle = i18n((item && item.title) || key);
                if (hasAccessToPackage("digitalsuite")) {
                    categoryHTML = ReactDOMServer.renderToString(
                        <ChartLabelOpenNewWindowWrapper>
                            <a
                                href={href}
                                data-ts-category={key}
                                title={chosenTitle}
                                target="_blank"
                                rel="noreferrer"
                            >
                                {categoryArray.map((cat, index) => (
                                    <span key={index}>
                                        {cat}
                                        <br />
                                    </span>
                                ))}
                            </a>
                        </ChartLabelOpenNewWindowWrapper>,
                    );
                }
            }
            return categoryHTML;
        };
    };

    const getChartConfig = () => {
        const config: any = {
            colors: Object.values(CHART_COLORS.trafficSourcesColorsBySourceMMX),
            chart: {
                height: 185,
                type: "column",
                marginRight: 0,
                marginLeft: 0,
                marginTop: 20,
                marginBottom: 36,
            },
            xAxis: {
                categories: getCategories(),
                lineColor: colorsPalettes.carbon[100],
                gridLineColor: colorsPalettes.carbon[50],
                tickWidth: 0,
                labels: {
                    align: "center",
                    rotation: 0,
                    useHTML: true,
                    style: {
                        fontSize: "13px",
                        textAlign: "center",
                        fontFamily: '"Roboto", sans-serif',
                    },
                    formatter: getRenderCategoryLabel(),
                },
            },
            yAxis: {
                gridLineColor: colorsPalettes.carbon[50],
                min: 0,
                max: 100,
                startOnTick: false,
                endOnTick: true,
                title: null,
                opposite: false,
                showLastLabel: true,
                tickAmount: 5,
                color: colorsPalettes.carbon[400],
                labels: {
                    enabled: false,
                },
            },
            legend: {
                enabled: false,
            },
            plotOptions: {
                column: {
                    borderWidth: 0,
                    pointWidth: 50,
                    minPointLength: 3,
                    grouping: true,
                    maxPointWidth: 40,
                    pointPadding: 0.15,
                    pointRange: 1,
                    animation: false,
                    dataLabels: {
                        enabled: true,
                        crop: false,
                        formatter: function () {
                            return this.point.y ? percentsFilter(this.point.y) : "N/A";
                        },
                        color: "#707070",
                        useHTML: true,
                        style: {
                            fontFamily: '"Roboto", sans-serif',
                            fontSize: "12px",
                            fontWeight: "400",
                        },
                        overflow: "none",
                    },
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                },
            },
            tooltip: {
                enabled: false,
            },
        };
        return config;
    };

    const checkIfNoData = () => {
        const isNoData = !data || isError || (Array.isArray(data) && data.length === 0);
        if (isNoData) {
            onEmptyData("qualification_traffic_sources");
        }
        return isNoData;
    };

    const getTitle = () => (
        <TitleWrapper marginBottom="0px">
            <WidgetTitle
                title={title}
                tooltip={tooltip}
                filters={subTitleFilters}
            />
        </TitleWrapper>
    );

    const onCtaClick = () => {
        allTrackers.dispatchTrackEvent("Internal Link", "click", "QualificationSidebar/channels/see_more");
    };

    const getContent = () => {
        const ctaHref = proNavigator.href(
            "digitalsuite_website_marketingchannels",
            {
                key: domain,
                country,
                duration,
                isWWW: "*",
                webSource,
            },
            {
                absolute: true,
            },
        );
        return checkIfNoData() ? (
            <ContentWrapper height="250px">
                <NoDataLandscape
                    title="qualification.sidebar.traffic.sources.no-data.title"
                    subtitle="qualification.sidebar.traffic.sources.no-data.subtitle"
                />
            </ContentWrapper>
        ) : (
            <>
                <ContentWrapper>
                    <GlobalStyle />
                    <Chart
                        type={"column"}
                        data={data}
                        config={getChartConfig()}
                    />
                </ContentWrapper>
                <CtaWrapper className={"submit-button"}>
                    <PlainTooltip
                        maxWidth={220}
                        tooltipContent={i18n("qualification.sidebar.traffic.sources.cta.tooltip")}
                    >
                        <a
                            href={ctaHref}
                            target="_blank"
                            rel="noreferrer"
                            onClick={onCtaClick}
                        >
                            <IconButton
                                type="outlined"
                                iconName="overview"
                                iconSize="xs"
                                style={{ textTransform: "inherit" }}
                            >
                                {i18n("qualification.sidebar.traffic.sources.cta")}
                            </IconButton>
                        </a>
                    </PlainTooltip>
                </CtaWrapper>
            </>
        );
    };

    return (
        <StyledBox
            maxHeight="385px"
            data-automation="qualification-sidebar-traffic-sources"
            ref={chartRef}
        >
            {isLoading ? (
                <ComponentsLoader />
            ) : (
                <>
                    {getTitle()} {getContent()}
                </>
            )}
        </StyledBox>
    );
};
export const MemoizedTrafficSources = React.memo(TrafficSources);
